import {
  cloneElement,
  useEffect,
  type MouseEvent,
  type PointerEvent,
  type ReactElement,
  type KeyboardEvent,
} from 'react';

/**
 * Menus and dialogs render only their trigger until someone reaches for it; the Radix primitive
 * (and its positioning and scroll-lock code) loads in the background after the page is idle, or
 * at once on hover, focus or press. Pages that show a trigger but never open it never pay for it.
 */
let gate: Promise<unknown> = Promise.resolve();

/**
 * Holds these background downloads until the app's first screen is complete (for example after
 * its data arrived), so they never compete with it; a pending API call leaves the page idle.
 */
export function holdPreloadsUntil(ready: Promise<unknown>): void {
  gate = ready;
}

export function preloadWhenIdle(load: () => Promise<unknown>): void {
  const run = () => {
    void load();
  };
  if (typeof window === 'undefined') return;
  void gate.then(() => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(run);
    else setTimeout(run, 1);
  });
}

export function useIdlePreload(load: () => Promise<unknown>): void {
  useEffect(() => {
    preloadWhenIdle(load);
  }, [load]);
}

type TriggerProps = {
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  onPointerEnter?: (event: PointerEvent<HTMLElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
};

/** The trigger as a plain element: warms the chunk on hover, calls `open` on press or Enter/Space/↓. */
export function standInTrigger(
  trigger: ReactElement,
  load: () => Promise<unknown>,
  open: () => void,
  popup: 'menu' | 'dialog',
): ReactElement {
  const own = trigger.props as TriggerProps;
  return cloneElement(
    trigger as ReactElement<TriggerProps>,
    {
      'aria-haspopup': popup,
      'aria-expanded': false,
      onPointerEnter: (event: PointerEvent<HTMLElement>) => {
        void load();
        own.onPointerEnter?.(event);
      },
      onClick: (event: MouseEvent<HTMLElement>) => {
        own.onClick?.(event);
        if (!event.defaultPrevented) open();
      },
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
        own.onKeyDown?.(event);
        if (popup === 'menu' && event.key === 'ArrowDown') {
          event.preventDefault();
          open();
        }
      },
    } as Partial<TriggerProps>,
  );
}
